import { useEffect, useRef } from 'react';
import { SoundManager, type WorldPosition } from './SoundManager';
import { eventSoundMap } from './sounds';
import { deriveAmbientTrack, deriveMusicTrack } from './useAudioDirector.js';

let globalManager: SoundManager | null = null;

export function getSoundManager(): SoundManager {
  if (!globalManager) {
    const source = (import.meta.env.VITE_AUDIO_SOURCE as 'synth' | 'file') ?? 'synth';
    globalManager = new SoundManager(source);
  }
  return globalManager;
}

/** Extract a vec3 position from an event payload, if present. */
function positionFromPayload(
  payload: Record<string, unknown> | undefined,
): WorldPosition | undefined {
  if (!payload) return undefined;
  for (const key of ['position', 'worldPos', 'pos'] as const) {
    const v = payload[key];
    if (Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === 'number')) {
      return v as unknown as WorldPosition;
    }
  }
  return undefined;
}

export function useSounds(
  events: ReadonlyArray<{ id?: number; type: string; payload?: Record<string, unknown> }>,
) {
  const lastId = useRef(-1);
  interface PendingSound {
    readonly id: string;
    readonly position?: WorldPosition;
  }
  const pendingRef = useRef<PendingSound[]>([]);

  useEffect(() => {
    const manager = getSoundManager();
    const handler = () => {
      if (!manager.isInitialized) {
        manager.init();
        for (const s of pendingRef.current) {
          manager.play(s.id, s.position);
        }
        pendingRef.current = [];
      }
    };
    document.addEventListener('click', handler, { once: true });
    document.addEventListener('keydown', handler, { once: true });
    return () => {
      document.removeEventListener('click', handler);
      document.removeEventListener('keydown', handler);
    };
  }, []);

  useEffect(() => {
    // Id watermark (events are tagged with monotonic ids and the history is
    // capped — see bootSim): process everything newer than the last id we
    // saw. A last id *below* our watermark means the match reset.
    if (events.length === 0) {
      lastId.current = -1;
      return;
    }
    const first = events[0]!;
    const last = events[events.length - 1]!;
    const lastWm = typeof last.id === 'number' ? last.id : events.length - 1;
    if (lastWm <= lastId.current) return;
    const firstWm = typeof first.id === 'number' ? first.id : 0;
    const startWm = firstWm > lastId.current + 1 ? firstWm : lastId.current + 1;
    lastId.current = lastWm;
    const manager = getSoundManager();
    for (const event of events) {
      const wm = typeof event.id === 'number' ? event.id : events.indexOf(event);
      if (wm < startWm) continue;
      const soundId = eventSoundMap[event.type];
      if (!soundId) continue;
      const position = positionFromPayload(event.payload);
      if (!manager.isInitialized) {
        pendingRef.current.push({ id: soundId, position });
      } else {
        manager.play(soundId, position);
      }
    }
  }, [events]);
}

export interface AudioDirectorInput {
  readonly musicMode: 'menu' | 'single' | 'combat' | 'victory' | 'defeat';
  readonly recentCombat: boolean;
  readonly doctrine: string | null;
  readonly cameraBiome: string | null;
  readonly cameraOverCity: boolean;
}

export function useAudioDirector(input: AudioDirectorInput) {
  const manager = getSoundManager();
  const musicTrack = deriveMusicTrack({
    mode: input.musicMode,
    recentCombat: input.recentCombat,
    doctrine: input.doctrine,
  });
  const ambientTrack = input.cameraBiome
    ? deriveAmbientTrack({ biome: input.cameraBiome, overCity: input.cameraOverCity })
    : null;

  // No cleanup: SoundManager.playMusic / playAmbient self-manage source
  // lifecycle (crossfades between track swaps). useAudioDirector is called
  // from the root App component, so unmount is rare and the AudioContext
  // tears down with the page anyway.
  useEffect(() => {
    if (!manager.isInitialized) return;
    manager.playMusic(musicTrack);
  }, [musicTrack, manager]);

  useEffect(() => {
    if (!manager.isInitialized) return;
    if (ambientTrack) manager.playAmbient(ambientTrack);
    else manager.stopAmbient();
  }, [ambientTrack, manager]);
}
